import api from './client';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface SignupPayload {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role?: 'admin' | 'agent' | 'user';
}

export interface AuthUser {
  id: number | string;
  name: string;
  email: string;
  role?: 'admin' | 'agent' | 'user';
  isApproved?: boolean;
}

interface RawAuthResponse {
  status: number;
  message?: string;
  error?: string | string[];
  user: AuthUser;
  token: string;
}

export interface AuthResult {
  token: string;
  user: AuthUser;
  message?: string;
}

const unwrap = (raw: RawAuthResponse): AuthResult => {
  const { token, user, status, error, message } = raw;
  if (status !== 1 || !token) {
    const msg = Array.isArray(error)
      ? error.join(', ')
      : error || 'Request failed';
    throw new Error(msg);
  }
  return { token, user, message };
};

/** POST /api/auth/login (admin-only enforcement). */
export const adminLogin = async (payload: LoginPayload): Promise<AuthResult> => {
  const res = await api.post<RawAuthResponse>('/auth/login', payload);
  const result = unwrap(res.data);
  if (result.user.role && result.user.role !== 'admin') {
    throw new Error('This account does not have admin access.');
  }
  return result;
};

/** POST /api/auth/login (agent-only enforcement). */
export const agentLogin = async (payload: LoginPayload): Promise<AuthResult> => {
  const res = await api.post<RawAuthResponse>('/auth/login', payload);
  const result = unwrap(res.data);
  if (result.user.role && result.user.role !== 'agent') {
    throw new Error('This account is not registered as a merchant.');
  }
  return result;
};

/** POST /api/auth/signup — automatically sets role='agent'. */
export const agentSignup = async (
  payload: Omit<SignupPayload, 'role'>
): Promise<AuthResult> => {
  const res = await api.post<RawAuthResponse>('/auth/signup', {
    ...payload,
    role: 'agent',
  });
  return unwrap(res.data);
};

interface ProfileResponse {
  status: number;
  message?: string;
  user: AuthUser;
}

/** GET /api/auth/profile — the signed-in user. */
export const getProfile = async (): Promise<AuthUser> => {
  const res = await api.get<ProfileResponse>('/auth/profile');
  return res.data.user;
};

export interface ProfileUpdate {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmNewPassword?: string;
}

/**
 * PUT /api/auth/profile — rename and/or change password. A password change
 * needs currentPassword; the backend checks it and the 6-character minimum.
 */
export const updateProfile = async (payload: ProfileUpdate): Promise<AuthUser> => {
  const res = await api.put<ProfileResponse>('/auth/profile', payload);
  return res.data.user;
};

interface StatusResponse {
  status: number;
  message?: string;
}

/** Kind of account a reset is limited to — the portal it was started from. */
export type ResetRole = 'admin' | 'agent';

/**
 * "Forgot password?" — POST /api/auth/forgot-password emails a 6-digit code
 * (valid 10 minutes). Fails with "No merchant/admin account is registered
 * with this email." when the email isn't that kind of account.
 */
export const requestPasswordReset = async (
  email: string,
  role: ResetRole
): Promise<string | undefined> => {
  const res = await api.post<StatusResponse>('/auth/forgot-password', { email, role });
  return res.data.message;
};

/** POST /api/auth/verify-otp — checks the emailed code without using it up. */
export const verifyResetOtp = async (email: string, otp: string, role: ResetRole): Promise<void> => {
  await api.post<StatusResponse>('/auth/verify-otp', { email, otp, role });
};

export interface ResetPasswordPayload {
  email: string;
  otp: string;
  password: string;
  confirmPassword: string;
  role: ResetRole;
}

/** POST /api/auth/reset-password — sets the new password with the code. */
export const resetPassword = async (payload: ResetPasswordPayload): Promise<string | undefined> => {
  const res = await api.post<StatusResponse>('/auth/reset-password', payload);
  return res.data.message;
};
